import { Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { SelectComponent } from '@shared/components/select/select.component';

export interface UploadZoneConfig {
  displayText: string;
  acceptedFileTypesText: string;
  acceptedFileTypes: string[];
  multiple: boolean;
  availableTransformers: string[];
  onlyCaptureFiles: boolean;
}

export interface UploadClicked {
  files: File[];
  transformer: string;
}

export interface FilesDropped {
  files: File[];
}

@Component({
  selector: 'app-upload-zone',
  imports: [FormsModule, TranslatePipe, SelectComponent],
  templateUrl: './upload-zone.component.html',
  styles: ``
})
export class UploadZoneComponent {
  private translate = inject(TranslateService);

  config = input.required<UploadZoneConfig>();

  filesDropped = output<FilesDropped>();
  uploadClicked = output<UploadClicked>();

  protected selectedTransformer = signal<string | undefined>(undefined);
  private selectedFiles = signal<File[]>([]);
  protected selectedFileName = computed(() =>
    this.selectedFiles()
      .map((f: File) => f.name)
      .join(', ')
  );

  protected hasUploadButton = computed(() => !this.config().onlyCaptureFiles);
  protected errorWhileUploading: string | null = null;
  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onUpload(): void {
    const filteredFiles = this.selectedFiles();
    const selectedTransformer = this.selectedTransformer();

    if (filteredFiles.length > 0 && !isNil(selectedTransformer)) {
      this.uploadClicked.emit({ files: filteredFiles, transformer: selectedTransformer });
      return;
    }

    if (isNil(selectedTransformer)) {
      this.errorWhileUploading = this.translate.instant(
        'upload_zone.error.transformer_not_selected'
      );
    }
    if (filteredFiles.length === 0) {
      this.errorWhileUploading = this.translate.instant(
        'upload_zone.error.not_accepted_file_type'
      );
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const cfg = this.config();

    const files = Array.from(event.dataTransfer?.files ?? []);

    if (!cfg.multiple && files.length > 1) {
      this.errorWhileUploading = this.translate.instant(
        'upload_zone.error.multiple_files_not_allowed'
      );
      return;
    }

    const filteredFiles = this.filterFilesByType(files);

    if (filteredFiles.length === 0) {
      this.errorWhileUploading = this.translate.instant(
        'upload_zone.error.not_accepted_file_type'
      );
    } else {
      if (cfg.onlyCaptureFiles) {
        this.filesDropped.emit({ files: filteredFiles });
      } else {
        this.selectedFiles.set(filteredFiles);
      }
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const cfg = this.config();
      const files = Array.from(input.files);
      const filteredFiles = this.filterFilesByType(files);

      if (filteredFiles.length === 0) {
        this.errorWhileUploading = this.translate.instant(
          'upload_zone.error.not_accepted_file_type'
        );
      } else {
        if (cfg.onlyCaptureFiles) {
          this.filesDropped.emit({ files: filteredFiles });
        } else {
          this.selectedFiles.set(filteredFiles);
        }
      }
    }
  }

  private filterFilesByType(files: File[]): File[] {
    const cfg = this.config();
    if (!cfg.acceptedFileTypes || cfg.acceptedFileTypes.length === 0) {
      return files;
    }

    return files.filter((file) => {
      return cfg.acceptedFileTypes.some((type) => {
        if (type.startsWith('.')) {
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        }
        return file.type === type;
      });
    });
  }
}
